
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, X, Image } from 'lucide-react';

interface PropertyImagesFormProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
}

const PropertyImagesForm = ({ images, onImagesChange }: PropertyImagesFormProps) => {
  const [newImageUrl, setNewImageUrl] = useState('');

  const addImage = () => {
    if (newImageUrl.trim() && !images.includes(newImageUrl.trim())) {
      onImagesChange([...images, newImageUrl.trim()]);
      setNewImageUrl('');
    }
  };

  const removeImage = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    onImagesChange(updatedImages);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addImage();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Imágenes</Label>
        <p className="text-sm text-gray-600 mb-2">
          Agrega URLs de Cloudinary para las imágenes de la propiedad
        </p>
      </div>

      {/* Add new image URL */}
      <div className="flex gap-2">
        <Input
          placeholder="https://res.cloudinary.com/..."
          value={newImageUrl}
          onChange={(e) => setNewImageUrl(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <Button
          type="button"
          onClick={addImage}
          disabled={!newImageUrl.trim()}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Images list */}
      {images.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Imágenes agregadas ({images.length})
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {images.map((imageUrl, index) => (
              <Card key={index} className="relative">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={`Imagen ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        Imagen {index + 1}
                      </p>
                      <p className="text-xs text-gray-500 break-all">
                        {imageUrl}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeImage(index)}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {images.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <Image className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No se han agregado imágenes aún</p>
          <p className="text-sm text-gray-400">
            Agrega las URLs de Cloudinary usando el campo de arriba
          </p>
        </div>
      )}
    </div>
  );
};

export default PropertyImagesForm;
